import React, { FC, useRef, useState } from 'react';
import Preview from 'components/posts/detail/overview/leftSection/Preview';
import Header from 'components/posts/detail/overview/Header';
import Popups from 'components/posts/detail/overview/footer/Popups';
import PostCompletion from 'components/posts/detail/overview/rightSection/infoCard/PostCompletion';
import Labels from 'components/posts/detail/overview/rightSection/infoCard/Labels';
import Notes from 'components/posts/detail/overview/rightSection/infoCard/Notes';
import BoostBudget from 'app/modules/posts/modules/post/components/overview/BoostBudget';
import PostRequirements from 'components/posts/detail/overview/rightSection/infoCard/PostRequirements';
import AssignedTasks from 'components/posts/detail/overview/rightSection/infoCard/AssignedTasks';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import InfoCardPostTranslation from 'components/posts/detail/overview/rightSection/infoCard/InfoCardPostTranslation';
import EntityDetailLayout from 'app/components/EntityDetailLayout';
import { usePostPopups } from 'app/modules/posts/modules/post/hooks/usePostPopups';
import { PostModalOnClose } from 'app/modules/posts/modules/post/hooks/usePostModal';
import { Alert, Button } from '@kontentino/ui';
import PostDetailModalFooter from 'app/modules/posts/modules/detail/components/PostDetailModalFooter';
import { usePostQuery } from 'app/modules/posts/modules/post/hooks/usePostQuery';
import { usePostDetailWorkflowHandlers } from 'app/modules/posts/modules/post/hooks/usePostDetailWorkflowHandlers';
import { getApprovalCommunicationTabForStatus } from 'app/modules/posts/utils/approvalCommunicationTab';
import PostDetailModalFooterSkeleton from 'app/modules/posts/modules/detail/components/postModalFooter/PostDetailModalFooterSkeleton';
import TimeUtils from 'app/utils/time';
import PostDetailPublishingMethod from 'app/modules/posts/modules/post/components/overview/PostDetailPublishingMethod';
import { ScheduleActionAnimationProvider } from './ScheduleActionAnimationProvider';
import { FirstCommentStatus } from 'app/modules/posts/constants';
import PostCommunication from './PostCommunication';
import WidgetsSetup from 'components/posts/detail/layout/widgetsSetup';
import { useWidgetsSetupApi } from 'components/posts/detail/hooks/useWidgetsSetupApi';
import { PostCommunicationRef } from './postCommunication/PostCommunicationProvider';
import PostOverviewLocalPost from 'app/modules/posts/modules/post/components/overview/PostOverviewLocalPost';
import PostOverviewLocalPosts from './overview/PostOverviewLocalPosts';
import { isLocalPostLocked } from 'app/modules/posts/utils/globalPosts';
import { Responses } from 'constants/responses';
import { Trans, useTranslation } from 'react-i18next';
import { PostsCommunicationEmptyMessage } from 'app/modules/posts/modules/post/components/postCommunication/PostsCommunicationEmptyMessage';
import DuplicateSelectedPost from '../../duplicatePosts/components/DuplicateSelectedPost';

type Props = {
  id: number;
  onClose: PostModalOnClose;
};

const PostOverview: FC<Props> = ({ id, onClose }) => {
  const { postDetail, postScheduleValidation } = usePostQuery(id, {
    common: { refetchInterval: TimeUtils.calcMilliseconds.minutes(1) },
  });
  const postCommunicationRef = useRef<PostCommunicationRef>(null);
  const { t } = useTranslation();
  const [isDuplicatePostsModalOpen, setIsDuplicatePostsModalOpen] =
    useState(false);

  const isLocaLPostLocked =
    postDetail.data && isLocalPostLocked(postDetail.data);

  const widgetsSetupApi = useWidgetsSetupApi({
    target: 'post-preview',
  });

  const popups = usePostPopups();

  const workflowHandlers = usePostDetailWorkflowHandlers(
    id,
    popups.showPopup,
    postCommunicationRef,
    postDetail.data?.page,
  );

  if (postDetail.error?.response?.status === Responses.ERROR.FORBIDDEN) {
    return (
      <EntityDetailLayout
        header={
          <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-4">
            <Alert
              title={t('postOverview.forbidden.title')}
              variant="danger"
              data-cy="post-overview-forbidden-alert"
              className="tw-max-w-md"
            >
              <Trans
                i18nKey="postOverview.forbidden.message"
                components={{ br: <br /> }}
              />
            </Alert>
            <Button onClick={() => onClose()}>{t('close')}</Button>
          </div>
        }
      />
    );
  }

  return (
    <>
      <ScheduleActionAnimationProvider>
        <WidgetsSetup.Root
          widgetsSetup={widgetsSetupApi.query.data}
          defaultWidgetsSetup={widgetsSetupApi.DEFAULT_WIDGET_SETUP}
          onWidgetsSetupChange={(setup) =>
            widgetsSetupApi.mutation.mutate(setup)
          }
          isLoading={widgetsSetupApi.query.isLoading}
        >
          <EntityDetailLayout
            header={
              <Header
                id={id}
                onClose={onClose}
                onDuplicatePostClick={() => setIsDuplicatePostsModalOpen(true)}
              />
            }
            contentLeft={<Preview id={id} />}
            contentRight={
              <WidgetsSetup.Layout>
                {{
                  publishingMethod: () => (
                    <>
                      {!isLocaLPostLocked && (
                        <>
                          {postDetail.data?.attachments.firstComment?.status ===
                            FirstCommentStatus.Failed && (
                            <Alert
                              title={t('postOverview.firstCommentFailed.title')}
                              variant="danger"
                              data-cy="post-overview-first-comment-publish-issue-alert"
                              data-name="post-overview-first-comment-publish-issue-alert"
                            >
                              {t('postOverview.firstCommentFailed.message')}
                            </Alert>
                          )}
                          <UserPermissionGate
                            scopes={UserPermission.POST_SCHEDULE}
                          >
                            <PostDetailPublishingMethod
                              postDetail={postDetail.data}
                            />
                          </UserPermissionGate>
                        </>
                      )}
                    </>
                  ),
                  communication: ({ isExpanded }) =>
                    postDetail.data ? (
                      <UserPermissionGate
                        exact={false}
                        scopes={[
                          UserPermission.POST_COMMUNICATION_CLIENT_SHOW,
                          UserPermission.POST_COMMUNICATION_INTERNAL_SHOW,
                        ]}
                      >
                        <PostCommunication
                          ref={postCommunicationRef}
                          enableRespondToApprovalRequest
                          dataName="post-preview_communication"
                          postId={id}
                          initialActiveContent={
                            getApprovalCommunicationTabForStatus(
                              postDetail.data.status,
                            ) ?? undefined
                          }
                          accordionProps={{ initialIsOpen: isExpanded }}
                          emptyState={<PostsCommunicationEmptyMessage />}
                        />
                      </UserPermissionGate>
                    ) : null,
                  translation: ({ isExpanded }) => {
                    if (!isLocaLPostLocked) {
                      return (
                        <InfoCardPostTranslation
                          id={id}
                          accordionProps={{ initialIsOpen: isExpanded }}
                        />
                      );
                    }
                    return <></>;
                  },
                  postCompletion: ({ isExpanded, setExpanded }) => {
                    if (!isLocaLPostLocked) {
                      return (
                        <UserPermissionGate
                          scopes={UserPermission.POST_SCHEDULE}
                        >
                          <PostCompletion
                            scheduleValidationMessages={
                              postScheduleValidation.data?.messages
                            }
                            postPreviewData={postDetail.data}
                            isLoading={
                              postDetail.isLoading ||
                              postScheduleValidation.isLoading
                            }
                            error={
                              postScheduleValidation?.error?.userMessage ||
                              postDetail?.error?.userMessage ||
                              postDetail.isError ||
                              postScheduleValidation.isError
                            }
                            accordionProps={{
                              initialIsOpen: isExpanded,
                              onOpenChange: setExpanded,
                            }}
                          />
                        </UserPermissionGate>
                      );
                    }
                    return <></>;
                  },
                  labels: ({ isExpanded }) => {
                    if (!isLocaLPostLocked) {
                      return (
                        <Labels
                          post={{
                            id,
                            tags: postDetail.data?.tags ?? [],
                            page: postDetail.data?.page,
                            status: postDetail.data?.status,
                          }}
                          isLoading={postDetail.isLoading}
                          isError={postDetail.isError}
                          accordionProps={{ initialIsOpen: isExpanded }}
                        />
                      );
                    }
                    return <></>;
                  },
                  notes: ({ isExpanded }) => {
                    if (!isLocaLPostLocked) {
                      return (
                        <UserPermissionGate
                          scopes={UserPermission.POST_SHOW_NOTE_IN_DETAIL}
                        >
                          <Notes
                            note={postDetail.data?.noteWithMentions}
                            isLoading={postDetail.isLoading}
                            isError={postDetail.isError}
                            accordionProps={{ initialIsOpen: isExpanded }}
                          />
                        </UserPermissionGate>
                      );
                    }
                    return <></>;
                  },
                  boostBudget: ({ isExpanded }) => {
                    if (!isLocaLPostLocked) {
                      return (
                        <BoostBudget
                          id={id}
                          accordionProps={{ initialIsOpen: isExpanded }}
                        />
                      );
                    }
                    return <></>;
                  },
                  postRequirements: ({ isExpanded }) => (
                    <UserPermissionGate
                      scopes={UserPermission.POST_SHOW_REQUIREMENTS}
                    >
                      <PostRequirements
                        id={id}
                        accordionProps={{ initialIsOpen: isExpanded }}
                      />
                    </UserPermissionGate>
                  ),
                  tasks: ({ isExpanded }) => (
                    <AssignedTasks
                      id={id}
                      showPopup={popups.showPopup}
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  localPosts: ({ isExpanded }) => (
                    <PostOverviewLocalPosts
                      id={id}
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  localPost: ({ isExpanded }) => {
                    if (postDetail.data?.globalPost) {
                      return (
                        <PostOverviewLocalPost
                          id={id}
                          accordionProps={{ initialIsOpen: isExpanded }}
                        />
                      );
                    }
                    return <></>;
                  },
                }}
              </WidgetsSetup.Layout>
            }
            popup={{
              isActive: popups.isActive,
              onHide: popups.hidePopup,
              children: (
                <Popups
                  active={popups.activePopup}
                  id={id}
                  hidePopup={popups.hidePopup}
                />
              ),
            }}
            footer={
              ((postDetail.isLoading || postDetail.isLoading) && (
                <PostDetailModalFooterSkeleton />
              )) ||
              (postDetail.data &&
                postDetail.data &&
                !isLocalPostLocked(postDetail.data) && (
                  <PostDetailModalFooter
                    post={postDetail.data}
                    scheduleValidationMessages={
                      postScheduleValidation.data?.messages
                    }
                    schedulingInfo={{
                      isLoading: postScheduleValidation.isLoading,
                      info: postDetail.data.scheduling_information,
                      canExport:
                        postScheduleValidation.data?.canExport ||
                        postDetail.data.page.isDemo,
                      canSchedule:
                        postScheduleValidation.data?.canSchedule ??
                        postDetail.data.page.isDemo,
                      canPublishNow:
                        postScheduleValidation.data?.canPublishNow ?? false,
                      canCreateDarkPost:
                        postScheduleValidation.data?.canPublishDarkPost ??
                        false,
                    }}
                    workflowEventHandlers={workflowHandlers}
                  />
                ))
            }
          />
        </WidgetsSetup.Root>
      </ScheduleActionAnimationProvider>

      {isDuplicatePostsModalOpen && postDetail.data && (
        <DuplicateSelectedPost
          open={true}
          onClose={() => setIsDuplicatePostsModalOpen(false)}
          post={postDetail.data}
        />
      )}
    </>
  );
};

export default PostOverview;
